import { html } from '../lib/html.js';
import { busy, toastError } from '../lib/ui.js';
import { fmtDay } from '../services/maintenances.js';
import { RATING_LABEL, reviews } from '../services/reviews.js';
import { Page, go } from './base.js';

/**
 * Avaliar um serviço (/avaliar/:maintenanceId): estrelas, etiquetas conforme a nota e comentário opcional.
 * Leva menos de 20 segundos. Aberta pelo pedido de avaliação (push/e-mail) ou pelos cartões do Início e do veículo.
 */
class ReviewFormPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/home" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Avaliar serviço</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding surface-page" fullscreen>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  get mid() {
    if (this.maintenanceId) return String(this.maintenanceId);
    return decodeURIComponent(location.pathname.match(/\/avaliar\/([^/?#]+)/)?.[1] ?? '');
  }

  setup() {
    const body = this.$('.body');
    body.addEventListener('click', (e) => {
      const star = e.target.closest('[data-star]');
      if (star) return this.setRating(Number(star.dataset.star));
      const tag = e.target.closest('[data-tag]');
      if (tag) {
        const k = tag.dataset.tag;
        this.picked.has(k) ? this.picked.delete(k) : this.picked.add(k);
        tag.classList.toggle('on', this.picked.has(k));
        tag.setAttribute('aria-pressed', String(this.picked.has(k)));
      }
      if (e.target.closest('[data-action="send"]')) this.send();
      if (e.target.closest('[data-action="home"]')) go('/home', 'root');
    });
  }

  async onEnter() {
    this.rating = 0;
    this.picked = new Set();
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const [p, t] = await Promise.all([reviews.pending(), this.tags ? { tags: this.tags } : reviews.tags()]);
      this.tags = t.tags;
      this.item = p.items.find((x) => x.maintenanceId === this.mid);
      if (!this.item) {
        body.innerHTML = html`
          <div class="panel empty-panel">
            <div class="empty-badge"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
            <strong>Nada para avaliar aqui</strong>
            <p>Este serviço já foi avaliado ou o prazo de 30 dias terminou.</p>
            <ion-button expand="block" class="empty-cta" data-action="home">Ir para o início</ion-button>
          </div>`.value;
        return;
      }
      this.render();
    } catch (e) {
      body.innerHTML = html`<div class="center"><p>${e.message}</p></div>`.value;
    }
  }

  render() {
    const it = this.item;
    this.$('.body').innerHTML = html`
      <div class="panel row">
        <span class="row-icon"><ion-icon name="storefront-outline"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">${it.workshop?.name ?? 'Empresa'}</span>
          <span class="row-sub">${it.services.join(', ')} · ${it.vehicle.label} · ${fmtDay(it.serviceDate)}</span>
        </div>
      </div>
      <section class="rv-form">
        <h1>Como foi o serviço?</h1>
        <div class="star-pick" role="radiogroup" aria-label="Nota">
          ${[1, 2, 3, 4, 5].map((n) => html`<button type="button" role="radio" aria-checked="${this.rating === n ? 'true' : 'false'}" aria-label="${n} ${n === 1 ? 'estrela' : 'estrelas'}: ${RATING_LABEL[n]}" data-star="${n}"><ion-icon name="${n <= this.rating ? 'star' : 'star-outline'}"></ion-icon></button>`)}
        </div>
        <p class="rating-label">${this.rating ? RATING_LABEL[this.rating] : 'Toque nas estrelas'}</p>
      </section>
      <div class="tags-slot"></div>
      <div class="comment-slot" ${this.rating ? '' : 'hidden'}>
        <label class="field-label" for="rv-comment">Conte mais <span>(opcional)</span></label>
        <textarea id="rv-comment" class="rv-textarea" maxlength="500" placeholder="O que a próxima pessoa precisa saber sobre essa empresa?"></textarea>
        <p class="foot-hint">Aparece como seu primeiro nome e a inicial do sobrenome, com o veículo e o serviço. A placa e seus dados nunca são mostrados.</p>
        <ion-button expand="block" class="send" data-action="send">Enviar avaliação</ion-button>
      </div>`.value;
    this.renderTags();
  }

  setRating(n) {
    const keep = this.$('#rv-comment')?.value ?? '';
    this.rating = n;
    // etiquetas trocam de lado conforme a nota: some o que não combina mais
    const allowed = new Set(this.options().map((t) => t.key));
    for (const k of [...this.picked]) if (!allowed.has(k)) this.picked.delete(k);
    this.render();
    this.$('#rv-comment').value = keep;
  }

  options() {
    if (this.rating >= 4) return this.tags.positive;
    if (this.rating && this.rating <= 2) return this.tags.negative;
    return this.rating ? [...this.tags.positive, ...this.tags.negative] : [];
  }

  renderTags() {
    const opts = this.options();
    this.$('.tags-slot').innerHTML = opts.length
      ? html`
        <h2 class="rv-sub">${this.rating >= 4 ? 'O que se destacou?' : this.rating <= 2 ? 'O que deu errado?' : 'Algum destaque?'}</h2>
        <div class="tags wrap rv-tags">${opts.map((t) => html`<button type="button" class="chip ${this.picked.has(t.key) ? 'on' : ''}" aria-pressed="${this.picked.has(t.key) ? 'true' : 'false'}" data-tag="${t.key}">${t.label}</button>`)}</div>`.value
      : '';
  }

  async send() {
    if (!this.rating) return;
    const comment = this.$('#rv-comment').value.trim();
    await busy(this.$('.send'), async () => {
      try {
        await reviews.create({ maintenanceId: this.item.maintenanceId, rating: this.rating, tags: [...this.picked], ...(comment ? { comment } : {}) });
        this.$('.body').innerHTML = html`
          <div class="panel empty-panel">
            <div class="empty-badge"><ion-icon name="checkmark-circle-outline"></ion-icon></div>
            <strong>Obrigado pela avaliação!</strong>
            <p>Ela já aparece no perfil ${this.item.workshop?.name ? `de ${this.item.workshop.name}` : 'da empresa'} e ajuda outros motoristas a escolher bem.</p>
            <ion-button expand="block" class="empty-cta" data-action="home">Voltar ao início</ion-button>
          </div>`.value;
      } catch (e) {
        toastError(e.message);
      }
    });
  }
}

customElements.define('page-review-form', ReviewFormPage);
